import { userErrorMessage } from 'app/core/error/user-error';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from 'app/core/auth/auth.service';
import {
    catchError,
    finalize,
    map,
    Observable,
    shareReplay,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { environment } from 'environments/environment';
import { SnackBarService } from '../snack-bar/snack-bar.service';
import { CommonService } from 'app/modules/shared/services/common';

/**
 * The token refresh that is under way. Every request that fails while it runs waits for it instead of starting a
 * refresh of its own, and gets its result - the new token, or the error when the refresh failed.
 */
let refresh$: Observable<string> | null = null;

/** Set once the sign-out has been started, so that requests failing together do not each start one. */
let signOutStarted = false;

function refreshToken(authService: AuthService): Observable<string> {
    if (!refresh$) {
        refresh$ = authService.forceRefreshSession().pipe(
            take(1),
            map((result) => {
                if (!result?.accessToken) {
                    throw new Error('Token refresh returned no access token');
                }
                return result.accessToken;
            }),
            tap(() => (signOutStarted = false)),
            finalize(() => (refresh$ = null)),
            shareReplay(1),
        );
    }
    return refresh$;
}

/**
 * Handling of "401 Unauthorized" and "403 Forbidden" answers (Taiga #736):
 *
 *  - 401: refresh the token once, send the request again, and sign the user out only if it is still 401.
 *  - 403: refresh the token once, send the request again, and show the message of the backend in a snackbar only if
 *    it is still 403. A 403 means "signed in, but not allowed", so it never signs the user out.
 *
 * Whatever else the repeated request answers is passed on to the caller unchanged: it neither signs the user out
 * nor shows a message here.
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const translocoService = inject(TranslocoService);
    const _snackBar = inject(SnackBarService);
    const _common = inject(CommonService);

    // Clone the request object
    let newReq = req.clone();

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.

    if (authService.accessToken) {
        newReq = req.clone({
            headers: req.headers
                .set('Authorization', 'Bearer ' + authService.accessToken)
                .set(
                    'Accept-Language',
                    translocoService.getActiveLang() || 'de',
                )
                .set('X-App-Version', environment.version),
        });
    }

    const showMessage = (error: unknown): void =>
        _snackBar.openSnackBar(
            userErrorMessage(error, (key) => _common.t(key)),
        );

    const signOutOnce = (): void => {
        if (signOutStarted) {
            return;
        }
        signOutStarted = true;
        authService.signOut(true).subscribe();
    };

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            if (
                !(error instanceof HttpErrorResponse) ||
                (error.status !== 401 && error.status !== 403) ||
                // the identity provider's own answers are the sign-in library's business; waiting for the refresh
                // here would wait for the very request that failed
                req.url.startsWith(environment.ssoUrl)
            ) {
                return throwError(() => error);
            }

            // BSI TR-03161-2 O.Auth_10 — once the active session has
            // run past its maximum duration, don't silently refresh
            // it again (that would extend it indefinitely via the
            // IdP's own SSO cookie); force a real login challenge
            // instead, bypassing the refresh/queue logic entirely.
            if (authService.hasExceededMaxActiveSession()) {
                authService.forceReAuthentication();
                return throwError(
                    () => new Error('Active session limit exceeded; re-authenticating.'),
                );
            }

            return refreshToken(authService).pipe(
                // the refresh itself failed: the session cannot be renewed
                catchError((refreshError) => {
                    if (error.status === 401) {
                        signOutOnce();
                    } else {
                        // 403: no sign-out; the user hears the backend's message about the original answer
                        showMessage(error);
                    }
                    return throwError(() => refreshError);
                }),
                // the refresh worked: send the request again with the new token
                switchMap((token) =>
                    next(
                        newReq.clone({
                            headers: newReq.headers.set(
                                'Authorization',
                                'Bearer ' + token,
                            ),
                        }),
                    ).pipe(
                        catchError((retryError) => {
                            if (retryError instanceof HttpErrorResponse) {
                                if (retryError.status === 401) {
                                    // still not authenticated with a fresh token
                                    signOutOnce();
                                } else if (retryError.status === 403) {
                                    // still not allowed with a fresh token
                                    showMessage(retryError);
                                }
                            }
                            return throwError(() => retryError);
                        }),
                    ),
                ),
            );
        }),
    );
};
